// SSE 解碼 — cs-copilot concierge 事件流(ai_concierge.md §4.3;cs/concierge.py 框法
// `data: {"type":…,…}\n\n`,type 在 json 內)。純函式,可 vitest;widget 以 dual-mode
// 消費(SSE 或舊 JSON),所以後端翻 SSE 時 web 不需停機、不會掛。

export interface KBResult {
  title: string;
  snippet: string;
  deep_link?: string | null;
}

export type ConciergeEvent =
  | { type: 'conversation'; id: string }
  | { type: 'token'; text: string }
  | { type: 'kb_result'; results: KBResult[] }
  | { type: 'subscription'; status: string; plan?: string; expired_time?: number; manage_deep_link?: string }
  | { type: 'ticket'; ticket_id: string; status: string; channel?: string }
  | { type: 'escalation'; mode: string; handle: string }
  | { type: 'done'; conversation_id?: string }
  | { type: 'error'; code: string; message: string }
  | { type: 'unknown'; raw: string };

/** 把一個 SSE data 區塊的 json 字串解成事件(未知 type → unknown,壞 json → null)。 */
export function decodeConciergeData(json: string): ConciergeEvent | null {
  let o: Record<string, unknown>;
  try {
    o = JSON.parse(json);
  } catch {
    return null;
  }
  if (!o || typeof o !== 'object') return null;
  const s = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
  switch (o.type) {
    case 'conversation':
      return { type: 'conversation', id: s(o.id) };
    case 'token':
      return { type: 'token', text: s(o.text) };
    case 'kb_result':
      return { type: 'kb_result', results: Array.isArray(o.results) ? (o.results as KBResult[]) : [] };
    case 'subscription':
      return {
        type: 'subscription',
        status: s(o.status),
        plan: typeof o.plan === 'string' ? o.plan : undefined,
        expired_time: typeof o.expired_time === 'number' ? o.expired_time : undefined,
        manage_deep_link: typeof o.manage_deep_link === 'string' ? o.manage_deep_link : undefined,
      };
    case 'ticket':
      return { type: 'ticket', ticket_id: s(o.ticket_id), status: s(o.status), channel: typeof o.channel === 'string' ? o.channel : undefined };
    case 'escalation':
      return { type: 'escalation', mode: s(o.mode), handle: s(o.handle) };
    case 'done':
      return { type: 'done', conversation_id: typeof o.conversation_id === 'string' ? o.conversation_id : undefined };
    case 'error':
      return { type: 'error', code: s(o.code, 'error'), message: s(o.message) };
    default:
      return typeof o.type === 'string' && o.type ? { type: 'unknown', raw: o.type } : null;
  }
}

/**
 * 串流解碼器:把 ReadableStream 解出的文字塊(可能切在任意位置)累積,每遇空行
 * 結束的 `data:` 區塊就吐一個事件。容錯 CRLF、`:` 心跳、多行 data、未知事件。
 */
export class ConciergeSSEDecoder {
  private buf = '';

  push(chunk: string): ConciergeEvent[] {
    this.buf += chunk.replace(/\r/g, '');
    const out: ConciergeEvent[] = [];
    let i: number;
    while ((i = this.buf.indexOf('\n\n')) >= 0) {
      const block = this.buf.slice(0, i);
      this.buf = this.buf.slice(i + 2);
      const ev = this.parseBlock(block);
      if (ev) out.push(ev);
    }
    return out;
  }

  flush(): ConciergeEvent[] {
    const block = this.buf;
    this.buf = '';
    const ev = this.parseBlock(block);
    return ev ? [ev] : [];
  }

  private parseBlock(block: string): ConciergeEvent | null {
    const data = block
      .split('\n')
      .filter((l) => l.startsWith('data:'))
      .map((l) => l.slice(5).replace(/^ /, '')) // strip "data:" + one optional leading space
      .join('\n');
    if (!data) return null;
    return decodeConciergeData(data);
  }
}
